const DEFAULT_SEGMENT_BYTE_SIZE = 512 * 1024;
const DEFAULT_SEGMENT_COUNT = 8;
const DEFAULT_MAX_LINES_PER_SEGMENT = 2048;
const NEWLINE_BYTE = 0x0a;
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();
function assertPositiveInteger(name, value) {
    if (!Number.isInteger(value) || value < 1) {
        throw new Error(`${name} must be a positive integer`);
    }
}
/**
 * Creates a bounded store of text lines, backed by a ring of pre-allocated
 * `Uint8Array` segments which lines are encoded into with
 * `TextEncoder.encodeInto`.
 *
 * Lines are written into the active segment until one does not fit, at which
 * point the segment is sealed and the next one becomes active. Once every
 * segment is in use the oldest is recycled, dropping its lines. Memory usage is
 * therefore bounded by `segmentByteSize * segmentCount`, regardless of how much
 * is logged.
 *
 * Because lines never straddle segments, a sealed segment is a self-contained
 * block of complete lines, which makes it a natural unit to hand over to
 * persistent storage via `onSegmentSealed`.
 *
 * Example usage:
 *
 * ```ts
 * const buffer = createTextRingBuffer({segmentByteSize: 1024, segmentCount: 2});
 * buffer.push('first\n');
 * buffer.push('second\n');
 * buffer.toString(); // 'first\nsecond\n'
 * ```
 *
 * @param options - See {@link TextRingBufferOptions}
 * @returns TextRingBuffer
 */
export function createTextRingBuffer({ segmentByteSize = DEFAULT_SEGMENT_BYTE_SIZE, segmentCount = DEFAULT_SEGMENT_COUNT, maxLinesPerSegment = DEFAULT_MAX_LINES_PER_SEGMENT, onSegmentSealed, } = {}) {
    assertPositiveInteger('segmentByteSize', segmentByteSize);
    assertPositiveInteger('segmentCount', segmentCount);
    assertPositiveInteger('maxLinesPerSegment', maxLinesPerSegment);
    let segments = [];
    let droppedLineCount = 0;
    let truncatedLineCount = 0;
    /**
     * Segments handed to `onSegmentSealed`, whose memory must not be recycled
     * until the call returns.
     */
    const sealingSegments = new Set();
    let notifying = false;
    const createSegment = () => ({
        bytes: new Uint8Array(segmentByteSize),
        lineEnds: new Uint32Array(maxLinesPerSegment),
        lineCount: 0,
        used: 0,
    });
    const resetSegment = (segment) => {
        segment.lineCount = 0;
        segment.used = 0;
    };
    const lineStart = (segment, index) => index === 0 ? 0 : (segment.lineEnds[index - 1] ?? 0);
    const appendLine = (segment, line) => {
        const { read, written } = textEncoder.encodeInto(line, segment.bytes.subarray(segment.used));
        if (read < line.length) {
            return false;
        }
        segment.used += written;
        segment.lineEnds[segment.lineCount] = segment.used;
        segment.lineCount += 1;
        return true;
    };
    const appendTruncatedLine = (segment, line) => {
        const endsWithNewline = line.endsWith('\n');
        const content = endsWithNewline ? line.slice(0, -1) : line;
        const target = endsWithNewline
            ? segment.bytes.subarray(0, segmentByteSize - 1)
            : segment.bytes;
        const { written } = textEncoder.encodeInto(content, target);
        segment.used = written;
        if (endsWithNewline) {
            segment.bytes[segment.used] = NEWLINE_BYTE;
            segment.used += 1;
        }
        segment.lineEnds[0] = segment.used;
        segment.lineCount = 1;
        truncatedLineCount += 1;
    };
    const notifySealed = (segment) => {
        if (!onSegmentSealed || notifying || segment.lineCount === 0) {
            return;
        }
        notifying = true;
        let taken = false;
        try {
            taken =
                onSegmentSealed({
                    bytes: segment.bytes.subarray(0, segment.used),
                    lineCount: segment.lineCount,
                }) === true;
        }
        catch {
            // Persisting a segment is best effort: a callee which throws must
            // not break the write which sealed the segment, so the segment is
            // kept in memory as if the callee had declined it.
            taken = false;
        }
        finally {
            notifying = false;
        }
        if (taken) {
            droppedLineCount += segment.lineCount;
            segments = segments.filter(entry => entry !== segment);
        }
    };
    const rotateSegment = () => {
        let recycled;
        while (segments.length >= segmentCount) {
            // The segments handed to `onSegmentSealed` are skipped, as the
            // callee may still be reading their memory. Skipping them leaves a
            // gap in the retained lines, but keeps memory bounded.
            const index = segments.findIndex(segment => !sealingSegments.has(segment));
            const [oldest] = index === -1 ? [] : segments.splice(index, 1);
            if (!oldest) {
                break;
            }
            droppedLineCount += oldest.lineCount;
            resetSegment(oldest);
            recycled = oldest;
        }
        const segment = recycled ?? createSegment();
        segments.push(segment);
        return segment;
    };
    /**
     * Drops the oldest segments until the buffer is back within capacity. Only
     * needed once `onSegmentSealed` has returned, as the segments handed to the
     * callee cannot be recycled while it reads them and are replaced instead.
     */
    const trimToCapacity = () => {
        while (segments.length > segmentCount) {
            const oldest = segments.shift();
            if (!oldest) {
                return;
            }
            droppedLineCount += oldest.lineCount;
        }
    };
    /**
     * Runs `seal` with `segment` protected from being recycled, so that the
     * memory handed to `onSegmentSealed` stays valid for the duration of the
     * call, even when the callee writes to the buffer.
     */
    const whileSealing = (segment, seal) => {
        if (!onSegmentSealed) {
            seal();
            return;
        }
        sealingSegments.add(segment);
        try {
            seal();
        }
        finally {
            sealingSegments.delete(segment);
            if (sealingSegments.size === 0) {
                trimToCapacity();
            }
        }
    };
    const getActiveSegment = () => {
        const active = segments.at(-1);
        if (active) {
            return active;
        }
        const segment = createSegment();
        segments.push(segment);
        return segment;
    };
    function push(line, contentWasTruncated = false) {
        if (line === '') {
            return;
        }
        const active = getActiveSegment();
        if (active.lineCount < maxLinesPerSegment && appendLine(active, line)) {
            if (contentWasTruncated) {
                truncatedLineCount += 1;
            }
            return;
        }
        if (active.lineCount === 0) {
            appendTruncatedLine(active, line);
            return;
        }
        // The active segment is sealed and replaced, and the line is written,
        // before `onSegmentSealed` is notified. The buffer is therefore in a
        // consistent state during the call, so a callee which logs appends to
        // the new active segment rather than sealing the same segment again.
        whileSealing(active, () => {
            const next = rotateSegment();
            if (appendLine(next, line)) {
                if (contentWasTruncated) {
                    truncatedLineCount += 1;
                }
            }
            else {
                appendTruncatedLine(next, line);
            }
            notifySealed(active);
        });
    }
    function flush() {
        const active = segments.at(-1);
        if (!active || active.lineCount === 0) {
            return;
        }
        // The segment is rotated out before the callee is notified, for the
        // same reason as in `push`: lines written from within the call must be
        // appended to the new active segment, never to the sealed one.
        whileSealing(active, () => {
            rotateSegment();
            notifySealed(active);
        });
    }
    function size() {
        return segments.reduce((total, segment) => total + segment.lineCount, 0);
    }
    function byteLength() {
        return segments.reduce((total, segment) => total + segment.used, 0);
    }
    function isEmpty() {
        return size() === 0;
    }
    function clear() {
        segments = [];
        droppedLineCount = 0;
        truncatedLineCount = 0;
    }
    function toBlobParts() {
        return segments
            .filter(segment => segment.used > 0)
            .map(segment => segment.bytes.subarray(0, segment.used));
    }
    function bufferToString() {
        return toBlobParts()
            .map(part => textDecoder.decode(part))
            .join('');
    }
    function* iterator() {
        for (const segment of segments) {
            for (let index = 0; index < segment.lineCount; index++) {
                yield textDecoder.decode(segment.bytes.subarray(lineStart(segment, index), segment.lineEnds[index]));
            }
        }
    }
    function toArray() {
        return [...iterator()];
    }
    return {
        push,
        size,
        byteLength,
        isEmpty,
        droppedLines: () => droppedLineCount,
        truncatedLines: () => truncatedLineCount,
        clear,
        flush,
        toBlobParts,
        toString: bufferToString,
        toArray,
        [Symbol.iterator]: iterator,
    };
}
//# sourceMappingURL=textRingBuffer.js.map